import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, ChevronDown, Lock, UserPlus, Users, LogOut, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';
import { useAvatarUri } from '../../services/avatar';
import Modal from '../ui/Modal';

interface HeaderProps {
  title: string;
}

export default function Header({ title }: HeaderProps) {
  const profile = useAuthStore((s) => s.profile);
  const reset = useAuthStore((s) => s.reset);
  const navigate = useNavigate();
  // La foto vive en un bucket privado desde la mig 211: hay que pedir permiso
  // de visionado. Si no llega, se pinta el icono genérico de siempre.
  const avatarUri = useAvatarUri(profile?.avatar_url);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modal, setModal] = useState<'password' | 'newDirector' | null>(null);
  // `null` = no se ha podido contar. No es 0: con 0 el globo de la campana no
  // se pinta, y una campana sin globo dice «no tienes avisos». El error se
  // caía en el destructuring de `{ count }` —la misma forma que el censo de
  // lecturas mudas, que solo mira `{ data` y por eso no la veía—.
  const [unread, setUnread] = useState<number | null>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Gestión de directores: SOLO el admin raíz (role='admin' estricto). Un
  // director NO ve "Crear director" ni el acceso a /directores.
  const isRoot = profile?.role === 'admin';

  // Change password
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwDone, setPwDone] = useState(false);

  // New director (alta vía EF create-director, root-only)
  const [directorForm, setDirectorForm] = useState({ email: '', full_name: '' });
  const [directorError, setDirectorError] = useState('');
  const [directorSaving, setDirectorSaving] = useState(false);
  const [directorDone, setDirectorDone] = useState(false);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [dropdownOpen]);

  // Badge unread — combinamos las notificaciones propias no leídas y no
  // descartadas con un canal Realtime para mantenerlo vivo entre páginas.
  // ⚠️ Este filtro `user_id = <yo>` era el síntoma, no el defecto: los anuncios
  // del club se escribían en UNA fila con `user_id IS NULL` y por eso no se
  // contaban aquí —una fila compartida no tiene `read` por persona—, así que el
  // anuncio no ponía el círculo rojo en ningún sitio. Desde la mig 330 el club
  // escribe una fila por persona (`anunciar_al_club`) y este recuento SÍ los ve;
  // no hace falta tocar nada, por eso se deja escrito.
  useEffect(() => {
    if (!profile?.id) return;
    const myId = profile.id;
    let cancelled = false;

    async function loadCount() {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', myId)
        .eq('read', false)
        .eq('dismissed', false);
      if (!cancelled) setUnread(error ? null : (count ?? 0));
    }

    void loadCount();
    const channel = supabase
      .channel(`notifs-unread-${myId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${myId}` },
        () => void loadCount()
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  function closeModal() {
    setModal(null);
    setPwForm({ current: '', next: '', confirm: '' });
    setPwError('');
    setPwDone(false);
    setShowPw({ current: false, next: false, confirm: false });
    setDirectorForm({ email: '', full_name: '' });
    setDirectorError('');
    setDirectorDone(false);
  }

  async function handleChangePassword() {
    setPwError('');
    if (!pwForm.current) return setPwError('Introduce tu contraseña actual');
    if (pwForm.next.length < 8)
      return setPwError('La nueva contraseña debe tener al menos 8 caracteres');
    if (pwForm.next !== pwForm.confirm) return setPwError('Las contraseñas no coinciden');

    setPwSaving(true);
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: profile?.email ?? '',
      password: pwForm.current,
    });
    if (signInErr) {
      setPwSaving(false);
      return setPwError('Contraseña actual incorrecta');
    }
    const { error } = await supabase.auth.updateUser({ password: pwForm.next });
    setPwSaving(false);
    if (error) return setPwError(error.message);
    setPwDone(true);
    setTimeout(closeModal, 2000);
  }

  // Alta de director vía EF create-director (D2), gated root-only en servidor.
  // Sustituye al antiguo flujo client-side (signUp + profiles.update({role:
  // 'admin'})) que asignaba el rol desde el cliente — agujero de escalada
  // cerrado: ahora el rol lo fija el servidor y solo el admin raíz puede
  // invocar la EF. El director recibe un email de invitación y define su
  // contraseña en /set-password.
  async function handleCreateDirector() {
    setDirectorError('');
    if (!directorForm.email) return setDirectorError('El email es obligatorio');
    if (!directorForm.full_name.trim()) return setDirectorError('El nombre es obligatorio');

    setDirectorSaving(true);
    try {
      const redirectTo = `${window.location.origin}/set-password`;
      const { data, error } = await supabase.functions.invoke('create-director', {
        body: {
          email: directorForm.email,
          full_name: directorForm.full_name,
          redirect_to: redirectTo,
        },
      });
      if (error) {
        let msg = 'Error al invitar al director';
        try {
          const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
          const body = await ctx?.json?.();
          msg = body?.error ?? error.message;
        } catch {
          msg = error.message;
        }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      setDirectorDone(true);
      setTimeout(closeModal, 3000);
    } catch (e) {
      setDirectorError(e instanceof Error ? e.message : 'Error al crear el director');
    } finally {
      setDirectorSaving(false);
    }
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    reset();
  }

  return (
    <header className="h-16 bg-bg-surface border-b border-bg-border flex items-center justify-between px-8 fixed top-0 left-64 right-0 z-10">
      <h1 className="font-oswald text-xl uppercase tracking-widest text-white">{title}</h1>

      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/notificaciones')}
          className="relative text-text-secondary hover:text-gold-primary transition-colors"
          aria-label="Notificaciones"
          data-testid="header-bell"
        >
          <Bell size={20} />
          {unread === null && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-error/20 text-error border border-error/40 text-[10px] font-bold flex items-center justify-center"
              title="No se han podido contar los avisos sin leer"
              data-testid="header-bell-sin-contar"
            >
              ?
            </span>
          )}
          {(unread ?? 0) > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold-primary text-bg-primary text-[10px] font-bold flex items-center justify-center"
              data-testid="header-bell-badge"
            >
              {(unread ?? 0) > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        {/* Account dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity focus:outline-none"
          >
            {avatarUri ? (
              <img
                src={avatarUri}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-bg-border"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-bg-elevated border border-bg-border flex items-center justify-center">
                <User size={16} className="text-text-secondary" />
              </div>
            )}
            <div className="hidden sm:block text-left">
              <p className="text-text-primary text-sm font-inter font-medium leading-none">
                {profile?.full_name ?? 'Admin'}
              </p>
              <p className="text-text-secondary text-xs mt-0.5">Administrador</p>
            </div>
            <ChevronDown
              size={14}
              className={`text-text-secondary transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-60 bg-bg-surface border border-bg-border rounded-xl shadow-xl z-50">
              <div className="px-4 py-3 border-b border-bg-border">
                <p className="text-white text-sm font-medium font-inter">{profile?.full_name}</p>
                <p className="text-text-secondary text-xs mt-0.5 font-inter">{profile?.email}</p>
              </div>
              <div className="py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    setModal('password');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-white hover:bg-bg-elevated transition-colors text-left"
                >
                  <Lock size={15} /> Cambiar contraseña
                </button>
                {isRoot && (
                  <>
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        setModal('newDirector');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-white hover:bg-bg-elevated transition-colors text-left"
                    >
                      <UserPlus size={15} /> Crear director
                    </button>
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        navigate('/directores');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-white hover:bg-bg-elevated transition-colors text-left"
                    >
                      <Users size={15} /> Gestionar directores
                    </button>
                  </>
                )}
                <div className="border-t border-bg-border my-1" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-error/10 transition-colors text-left"
                >
                  <LogOut size={15} /> Cerrar sesión
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cambiar contraseña */}
      <Modal open={modal === 'password'} onClose={closeModal} title="Cambiar contraseña" size="sm">
        {pwDone ? (
          <p className="text-success text-center py-4 font-inter">
            ✅ Contraseña actualizada correctamente
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label">Contraseña actual</label>
              <div className="relative">
                <input
                  type={showPw.current ? 'text' : 'password'}
                  className="input pr-10"
                  value={pwForm.current}
                  onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => ({ ...s, current: !s.current }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white"
                >
                  {showPw.current ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">Nueva contraseña</label>
              <div className="relative">
                <input
                  type={showPw.next ? 'text' : 'password'}
                  className="input pr-10"
                  value={pwForm.next}
                  onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => ({ ...s, next: !s.next }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white"
                >
                  {showPw.next ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">Confirmar nueva contraseña</label>
              <div className="relative">
                <input
                  type={showPw.confirm ? 'text' : 'password'}
                  className="input pr-10"
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => ({ ...s, confirm: !s.confirm }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white"
                >
                  {showPw.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {pwError && <p className="text-error text-sm font-inter">{pwError}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={closeModal} className="btn-ghost">
                Cancelar
              </button>
              <button onClick={handleChangePassword} disabled={pwSaving} className="btn-gold">
                {pwSaving ? 'Verificando...' : 'Cambiar contraseña'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Crear director (root-only; alta vía EF create-director) */}
      <Modal open={modal === 'newDirector'} onClose={closeModal} title="Crear director" size="sm">
        {directorDone ? (
          <div className="text-center py-4 space-y-2">
            <p className="text-success font-inter">✅ Invitación enviada correctamente</p>
            <p className="text-text-secondary text-xs font-inter">
              El director recibirá un email para definir su contraseña y acceder al panel.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label">Nombre completo</label>
              <input
                type="text"
                className="input"
                placeholder="Nombre Apellido"
                value={directorForm.full_name}
                onChange={(e) => setDirectorForm((f) => ({ ...f, full_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                placeholder="director@vertexperformance.es"
                value={directorForm.email}
                onChange={(e) => setDirectorForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            {directorError && <p className="text-error text-sm font-inter">{directorError}</p>}
            <p className="text-text-secondary text-xs font-inter">
              Se enviará una invitación por email. El director define su contraseña en el enlace y
              accede al panel con el mismo acceso que un administrador.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={closeModal} className="btn-ghost">
                Cancelar
              </button>
              <button onClick={handleCreateDirector} disabled={directorSaving} className="btn-gold">
                {directorSaving ? 'Enviando...' : 'Crear director'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </header>
  );
}
